import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ConviteDaApi, type MensagemDaApi } from './api';
import { chaves } from './consultas';

/**
 * Calendario. O servidor guarda UTC e expande as series (ADR 0071); aqui tudo
 * e exibido no fuso do navegador. Semana comeca na segunda (pt-BR).
 */

export type Frequencia = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
export type DiaDaSemana = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

/** Espelho do contrato de `rrule` (ADR 0071). */
export interface Regra {
  freq: Frequencia;
  interval: number;
  byday?: DiaDaSemana[];
  /** Ultimo dia, AAAA-MM-DD, inclusivo. */
  until?: string;
  count?: number;
}

/** A serie (ou o evento unico), como `POST`/`PATCH` devolvem. */
export interface EventoDaApi {
  id: string;
  title: string;
  location: string | null;
  description: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  rrule: Regra | null;
  tz: string | null;
  reminderMinutes: number | null;
}

/** Uma ocorrencia da janela, com os dados da serie para editar a serie inteira. */
export interface OcorrenciaDaApi extends Omit<EventoDaApi, 'id'> {
  eventoId: string;
  ocorrenciaEm: string;
  inicio: string;
  fim: string;
}

export interface DadosDeEvento {
  title: string;
  location: string | null;
  description: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  rrule: Regra | null;
  tz: string | null;
  reminderMinutes?: number | null;
}

/** Chave unica da ocorrencia: a serie e o inicio gerado pela regra. */
export const chaveDaOcorrencia = (o: OcorrenciaDaApi) => `${o.eventoId}|${o.ocorrenciaEm}`;

export const fusoDoNavegador = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export type VisaoDoCalendario = 'month' | 'week';

export const CHAVE_CALENDARIO = ['calendario'] as const;

export function inicioDoDia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function somarDias(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
}

/** Segunda-feira da semana de `d`, a meia-noite local. */
export function inicioDaSemana(d: Date): Date {
  const dia = inicioDoDia(d);
  return somarDias(dia, -((dia.getDay() + 6) % 7));
}

/** 42 dias (6 semanas) que cobrem o mes de `ref`, comecando na segunda. */
export function gradeDoMes(ref: Date): Date[] {
  const inicio = inicioDaSemana(new Date(ref.getFullYear(), ref.getMonth(), 1));
  return Array.from({ length: 42 }, (_, i) => somarDias(inicio, i));
}

export function diasDaSemana(ref: Date): Date[] {
  const inicio = inicioDaSemana(ref);
  return Array.from({ length: 7 }, (_, i) => somarDias(inicio, i));
}

export function mesmoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Janela [from, to) enviada ao servidor: a grade inteira, no maximo 42 dias. */
export function janelaDe(visao: VisaoDoCalendario, ref: Date): { from: string; to: string } {
  const dias = visao === 'month' ? gradeDoMes(ref) : diasDaSemana(ref);
  return {
    from: dias[0]!.toISOString(),
    to: somarDias(dias[dias.length - 1]!, 1).toISOString(),
  };
}

export function useEventos(visao: VisaoDoCalendario, ref: Date) {
  const { from, to } = janelaDe(visao, ref);
  return useQuery({
    queryKey: [...CHAVE_CALENDARIO, from, to],
    queryFn: () =>
      api<{ items: OcorrenciaDaApi[] }>(
        `/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    staleTime: 30_000,
  });
}

export function useCriarEvento() {
  const cliente = useQueryClient();
  return useMutation<EventoDaApi, Error, DadosDeEvento>({
    mutationFn: (corpo) => api<EventoDaApi>('/calendar/events', { method: 'POST', body: corpo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_CALENDARIO }),
  });
}

export function useAlterarEvento() {
  const cliente = useQueryClient();
  return useMutation<EventoDaApi, Error, { id: string; dados: DadosDeEvento }>({
    mutationFn: ({ id, dados }) =>
      api<EventoDaApi>(`/calendar/events/${id}`, { method: 'PATCH', body: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_CALENDARIO }),
  });
}

/** Sem `ocorrencia`, apaga a serie inteira; com, so aquela ocorrencia. */
export function useExcluirEvento() {
  const cliente = useQueryClient();
  return useMutation<void, Error, { id: string; ocorrencia?: string }>({
    mutationFn: ({ id, ocorrencia }) =>
      api<void>(
        `/calendar/events/${id}${ocorrencia === undefined ? '' : `?ocorrencia=${encodeURIComponent(ocorrencia)}`}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_CALENDARIO }),
  });
}

/** Itens (instantes em ms) que tocam o dia local `dia`. */
export function tocaODia(inicio: number, fim: number, dia: Date): boolean {
  const ini = inicioDoDia(dia).getTime();
  const fimDoDia = inicioDoDia(somarDias(dia, 1)).getTime();
  return inicio < fimDoDia && (fim > ini || (fim === inicio && inicio >= ini));
}

export function eventosDoDia(eventos: OcorrenciaDaApi[], dia: Date): OcorrenciaDaApi[] {
  return eventos.filter((e) =>
    tocaODia(new Date(e.inicio).getTime(), new Date(e.fim).getTime(), dia),
  );
}

const dois = (n: number) => String(n).padStart(2, '0');

/** Valor de `date` (AAAA-MM-DD) no fuso local. */
export function paraCampoDeDia(d: Date): string {
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
}

/** Valor de `datetime-local` no fuso local. */
export function paraCampoDeHora(d: Date): string {
  return `${paraCampoDeDia(d)}T${dois(d.getHours())}:${dois(d.getMinutes())}`;
}

/**
 * Dia inteiro: do primeiro instante do dia inicial ate o ultimo ms do dia final,
 * ambos no fuso local, guardados em UTC.
 */
export function intervaloDeDiaInteiro(diaInicial: string, diaFinal: string) {
  const [a, m, d] = diaInicial.split('-').map(Number) as [number, number, number];
  const [a2, m2, d2] = diaFinal.split('-').map(Number) as [number, number, number];
  return {
    startsAt: new Date(a, m - 1, d).toISOString(),
    endsAt: new Date(a2, m2 - 1, d2 + 1, 0, 0, 0, -1).toISOString(),
  };
}

export function horaCurta(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export type AcaoDoConvite = 'agenda' | 'aceitar' | 'talvez' | 'recusar';

/**
 * Por na agenda (ou tirar, em convite cancelado) e responder. O servidor relê o
 * convite da mensagem; daqui vai so a escolha e o fuso, para hora flutuante e
 * dia inteiro cairem no dia certo.
 */
export function useAcaoDoConvite(messageId: string) {
  const cliente = useQueryClient();
  return useMutation<{ jaNaAgenda: boolean }, Error, AcaoDoConvite>({
    mutationFn: (acao) => {
      const fuso = fusoDoNavegador();
      return acao === 'agenda'
        ? api(`/messages/${messageId}/invite/calendar`, { method: 'POST', body: { fuso } })
        : api(`/messages/${messageId}/invite/reply`, {
            method: 'POST',
            body: { fuso, resposta: acao },
          });
    },
    onSuccess: ({ jaNaAgenda }) => {
      cliente.setQueryData<MensagemDaApi>(chaves.mensagem(messageId), (m) =>
        m?.convite == null ? m : { ...m, convite: { ...m.convite, jaNaAgenda } },
      );
      void cliente.invalidateQueries({ queryKey: CHAVE_CALENDARIO });
    },
  });
}

/** Data de dia inteiro (AAAA-MM-DD) como meia-noite local, sem passar pelo UTC. */
function diaLocal(iso: string): Date {
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(a, m - 1, d);
}

/** "Quando" do convite no fuso de quem le. Hora sem fuso ja e local para o `Date`. */
export function quandoDoConvite(c: Pick<ConviteDaApi, 'inicio' | 'fim' | 'diaInteiro'>): string {
  if (c.diaInteiro) {
    const dia = (d: Date) =>
      d.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    const inicio = diaLocal(c.inicio);
    // O fim do iCalendar e exclusivo: o ultimo dia e a vespera dele.
    const ultimo = c.fim === null ? inicio : somarDias(diaLocal(c.fim), -1);
    return ultimo > inicio
      ? `${dia(inicio)} a ${dia(ultimo)} (dia inteiro)`
      : `${dia(inicio)} (dia inteiro)`;
  }
  const inicio = new Date(c.inicio);
  const fim = c.fim === null ? null : new Date(c.fim);
  const completo = (d: Date) =>
    d.toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'short' });
  if (fim === null || fim.getTime() === inicio.getTime()) return completo(inicio);
  return mesmoDia(inicio, fim)
    ? `${completo(inicio)} – ${horaCurta(fim.toISOString())}`
    : `${completo(inicio)} – ${completo(fim)}`;
}
